import { FC, useContext, useEffect, useState } from 'react';

import { Button, Input, Modal, Select } from '@smartshell/ui-kit';
import { useForm, Controller } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import { LAUNCHERS } from '../constants';
import { GameAccountsContext } from '../GameAccounts';
import {
  IFormGameAccountsGroupValues,
  IManageGameAccountsGroupMWProps,
} from '../interfaces';
import { renderLauncherOptions } from '../utils';
import s from './ManageGameAccountsGroupMW.module.scss';

export const ManageGameAccountsGroupMW: FC<IManageGameAccountsGroupMWProps> = ({
  onClose,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    createAccountsGroup,
    accountsGroupForEdit,
    updateAccountsGroup,
    deleteAccountsGroup,
  } = useContext(GameAccountsContext) ?? {};

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<IFormGameAccountsGroupValues>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      launcher: null,
    },
  });

  const [isRemoveInProgress, setIsRemoveInProgress] = useState(false);

  useEffect(() => {
    if (accountsGroupForEdit) {
      const { title, launcher } = accountsGroupForEdit;

      reset({
        title,
        launcher,
      });
    }
  }, [accountsGroupForEdit, LAUNCHERS]);

  return (
    <Modal onClose={onClose} disableOutsideClose>
      <form
        onSubmit={handleSubmit(({ title, launcher }) => {
          if (!createAccountsGroup || !updateAccountsGroup || !launcher) {
            return;
          }

          if (accountsGroupForEdit) {
            const { id } = accountsGroupForEdit;

            return updateAccountsGroup(id, title, launcher).then(onClose);
          }

          return createAccountsGroup(title, launcher).then(onClose);
        })}
        className={s.form}
      >
        <header className={s.header}>
          {t(accountsGroupForEdit ? 'group.editing' : 'gameAccount.modal.add')}
        </header>
        <div className={s.fields}>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('title')}
                placeholder="CS:GO"
                error={error?.message}
              />
            )}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 3,
                message: t('errors.minLength', 3),
              },
              maxLength: {
                value: 24,
                message: t('errors.maxLength', 24),
              },
            }}
          />
          <Controller
            name="launcher"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                placeholder={t('accounts.group.launcher')}
                options={LAUNCHERS}
                error={error?.message}
                renderOption={renderLauncherOptions}
              />
            )}
            rules={{
              required: t('errors.required'),
            }}
          />
        </div>
        <div className={s.controls}>
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={!isDirty || isRemoveInProgress}
            fullWidth
          >
            {t(accountsGroupForEdit ? 'save' : 'add')}
          </Button>
          {accountsGroupForEdit && (
            <Button
              onClick={() => {
                if (!deleteAccountsGroup) {
                  return;
                }

                setIsRemoveInProgress(true);
                deleteAccountsGroup(accountsGroupForEdit.id)
                  .then(onClose)
                  .finally(() => setIsRemoveInProgress(false));
              }}
              variant="secondary"
              color="red"
              className={s.remove}
              fullWidth
              disabled={
                isSubmitting ||
                isRemoveInProgress ||
                accountsGroupForEdit.accounts.length > 0
              }
            >
              {t('delete')}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};
